/**
 * SuperAdmin Sidebar Component
 * Custom sidebar for Super Admin dashboard with logo, navigation, and user profile
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ApartmentOutlined,
  PlusOutlined,
  BarChartOutlined,
  SettingOutlined,
  CloseOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Avatar, Tooltip } from 'antd';
import './SuperAdminSidebar.css';

const nacLogoUrl = `${process.env.PUBLIC_URL || ''}/nac_logo_tight_circle.png`;

interface SuperAdminSidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  onCreateCompany?: () => void;
  user?: {
    name?: string;
    email?: string;
  };
}

const SuperAdminSidebar: React.FC<SuperAdminSidebarProps> = ({ 
  collapsed: collapsedProp,
  onCollapse,
  onCreateCompany,
  user = { name: 'Super Admin', email: 'admin@system.bt' }
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const collapsed = collapsedProp !== undefined ? collapsedProp : internalCollapsed;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleCollapse = () => {
    const newCollapsed = !collapsed;
    if (collapsedProp === undefined) {
      setInternalCollapsed(newCollapsed);
    }
    onCollapse?.(newCollapsed);
  };

  const menuItems = [
    {
      key: '/superadmin',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/superadmin/companies',
      icon: <ApartmentOutlined />,
      label: 'Company Details',
    },
    {
      key: '/superadmin/create',
      icon: <PlusOutlined />,
      label: 'Add Company',
    },
    {
      key: '/superadmin/analytics',
      icon: <BarChartOutlined />,
      label: 'Analytics',
    },
    {
      key: '/superadmin/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
  ];

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  const isActive = (key: string) => {
    if (key === '/superadmin') {
      return location.pathname === '/superadmin' || location.pathname === '/superadmin/';
    }
    return location.pathname === key || location.pathname.startsWith(key + '/');
  };

  return (
    <>
      {isMobile && !collapsed && (
        <div
          className="superadmin-sidebar-overlay"
          onClick={handleCollapse}
        />
      )}
      <div 
        className={`superadmin-sidebar ${collapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}
      >
        {/* Header Section */}
        <div className="superadmin-sidebar-header">
          {!collapsed && (
            <div className="superadmin-sidebar-logo-section">
              <div className="superadmin-sidebar-logo-wrap">
                <img 
                  src={nacLogoUrl} 
                  alt="NAC Logo" 
                  className="superadmin-sidebar-logo"
                />
              </div>
              <div className="superadmin-sidebar-title">
                <div className="superadmin-sidebar-title-main">Super Admin Panel</div>
                <div className="superadmin-sidebar-title-sub">Control Panel</div>
              </div>
            </div>
          )}
          <button 
            className="superadmin-sidebar-close"
            onClick={handleCollapse}
            aria-label={collapsed ? "Open sidebar" : "Close sidebar"}
          >
            <span className="superadmin-sidebar-close-icon">
              {collapsed ? <MenuOutlined /> : <CloseOutlined />}
            </span>
          </button>
        </div>

        {/* Separator */}
        {!collapsed && <div className="superadmin-sidebar-separator" />}

        {/* Navigation Menu */}
        <div className="superadmin-sidebar-menu">
          {menuItems.map((item) => {
            const menuItem = (
              <div
                key={item.key}
                className={`superadmin-sidebar-menu-item ${isActive(item.key) ? 'active' : ''}`}
                onClick={() => handleMenuClick(item.key)}
              >
                <span className="superadmin-sidebar-menu-icon">{item.icon}</span>
                {!collapsed && (
                  <span className="superadmin-sidebar-menu-label">{item.label}</span>
                )}
              </div>
            );

            return collapsed ? (
              <Tooltip key={item.key} title={item.label} placement="right">
                {menuItem}
              </Tooltip>
            ) : (
              menuItem
            );
          })}
        </div>

        {/* User Profile Section */}
        <div className="superadmin-sidebar-user-section">
          <div className="superadmin-sidebar-user-info">
            <Avatar 
              size={collapsed ? 32 : 40}
              className="superadmin-sidebar-avatar"
              style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              SA
            </Avatar>
            {!collapsed && (
              <div className="superadmin-sidebar-user-details">
                <div className="superadmin-sidebar-user-name">{user.name || 'Super Admin'}</div>
                <div className="superadmin-sidebar-user-email">{user.email || 'admin@system.bt'}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SuperAdminSidebar;
